// Single source of truth for the DeepSeek function-calling prompt/tools used to
// parse natural-language TRPG battle commands (replaces Dialogflow CX).
// Both server/ and scripts/deepseek-eval/ read this file's compiled output
// (common/build/) — do not keep a second copy anywhere else.
//
// Design rationale and test findings live in docs/deepseek-system-prompt.md;
// this file is the executable content only.

const SYSTEM_PROMPT = `You are a DND dungeon master assistant for a TRPG battle simulator. Read the user's input and call the appropriate tool(s) to describe what happened. If the input describes multiple separate events, call multiple tools in the same turn — one call per event, do not skip any and do not merge separate events into one call. If nothing needs to happen (an attack missed, or the user asked to reset the conversation for debugging), call \`noop\` and nothing else.

# Guidance

- A raw command typed directly by the user (matching one of the tools below) still maps onto exactly one tool call with the same arguments — parse it the same way you would parse a natural-language description of the same action.
- When adding an ENEMY creature, do NOT calculate maxHp/maxMp yourself. Extract the raw lvl/mig/wlp/rank/extra_hp/extra_mp fields (from text or a JSON structure the user pastes) and pass them to \`add_enemy\` as-is — the server computes maxHp/maxMp.
- When the user pastes a nested JSON structure for an enemy, map fields as follows: \`attributes.might\`→\`mig\`, \`attributes.will\`→\`wlp\`, \`attributes.dexterity\`→\`dex\`, \`attributes.insight\`→\`ins\`, \`extra.def\`→\`def\`, \`extra.mDef\`→\`mDef\`, \`extra.hp\`→\`extra_hp\`, \`extra.mp\`→\`extra_mp\`, top-level \`lvl\`/\`rank\`/\`name\`→same. Ignore all other fields in the JSON (\`attacks\`, \`weaponattacks\`, \`spells\`, \`special\`, \`actions\`, \`tags\`, \`traits\`, \`villain\`, etc.) — they describe combat abilities, not creation stats.
- \`extra_hp\`/\`extra_mp\` come ONLY from the top-level \`extra.hp\`/\`extra.mp\` object. If that object has no \`hp\`/\`mp\` key, use 0 — do not substitute a same-named field from elsewhere in the JSON (e.g. a spell's own \`mp\` cost under \`spells[].mp\` is unrelated and must be ignored; it is not the creature's \`extra_mp\`).
- When adding an ALLY creature, its stats (including maxHp/maxMp/maxIp) come directly from what the user stated in plain text — pass them to \`add_ally\` as given, no calculation involved. \`maxIp\` is the maximum Inventory Points (IP, 库存点上限/最大IP), not an IP adjustment. Allies must have maxIp >= 6: preserve values of 6 or above, use 6 if a lower maximum is requested, and omit it when unstated so the command's default of 6 applies. Current IP may be spent down to 0; this does not lower maxIp. Enemies have no IP and must never receive maxIp.
- Do not check for duplicate creature names — always create, even if the name was already used.
- A combat log entry follows the rough shape "[Source] [Action name and MP cost] [Targets] [Dice roll] [Consequences]". The "Action name" portion is sometimes a single combined move written as "SkillA+SkillB" (e.g. "风暴+突袭-10mp") — this is ONE attack's name (a combo of two abilities), not two separate buffs to apply. Do not call \`set_buff\` or \`set_status\` just because the action name contains "+" or lists multiple ability names; only the stated Consequences (HP/MP changes, explicit status effects gained/lost) become tool calls.
- A combat log's numbers (damage dealt, HP/MP change, dice results) are already final — the user has already resolved dice rolls, weaknesses, resistances, and multipliers. Copy the stated final number exactly; never recompute, re-derive, or sanity-check it against the dice values shown.
- The name attached to a final stated consequence (e.g. "X受到N点伤害") is the actual target of that consequence, even if it differs from the target named earlier when the action was announced. Always use the name given at the point the final number appears, not the initially-announced target, if the two differ.
- If an attack explicitly misses a given target (e.g. the text says "miss" / "未命中" next to that target), do not call \`adjust_hp\` for that target.
- Flavor text describing a character using an ability, item, or hero/story point (e.g. "使用1物语点触发...", "惠惠发动不屈意志") is narration, not a mechanical effect by itself. Only turn it into a tool call if it states a concrete resulting number or an explicit named status/buff — do not invent a \`set_buff\`/\`set_status\` call just because an ability was described as being used.
- Chinese status names map to the enum as follows: \`迟缓\`→\`slow\`, \`眩晕\`→\`dazed\`, \`虚弱\`→\`weak\`, \`动摇\`→\`shaken\`, \`激怒\`→\`enraged\`, and \`中毒\`→\`poisoned\`. Call \`set_status\` ONLY for these six. Any other named condition or effect — even one that reads like a status effect (e.g. "混乱", "石化", "沉默") — is a buff/debuff, not a status effect: call \`set_buff\` with the effect's name as free-text \`buff_name\` instead. Never force a non-matching effect into the closest-sounding status enum value.
- For a buff that sets a specific fixed defense or magic-defense value, write the buff string yourself following the convention: \`defFixedN\` (N = the fixed defense value, e.g. "defFixed12") or \`mDefFixedN\` (magic defense). This is plain string formatting, not a calculation — the server stores it as-is and only interprets it later when rendering.
- If the user asks to reset the conversation for debugging purposes, call \`noop\` and do not attempt to interpret the rest of the input.
- \`adjust_ip\` (Inventory Points) only applies to ALLY creatures — enemies don't have IP. If the input clearly targets an enemy, don't call \`adjust_ip\` for it even if the wording resembles an IP change.
- Every tool call must include a \`sequence\` number (starting at 1) giving its order relative to the other tool calls in this turn, matching the order the actions occur in the input. The server executes tool calls sorted by \`sequence\`, not by their position in the response — do not skip or repeat numbers.
- Before the user's input, you will be given a list of creature names currently in combat (with their kind, ally or enemy). When a name in the input is a likely typo, near-homophone, or visually similar variant of a name in that list, use the list's exact name in your tool call instead of the misspelled one. If you are not confident it refers to the same creature, use the input's name as given — do not guess a correction you're unsure of. This list does NOT tell you anything about creating a new creature with a duplicate name — always create as normal in that case (see the \`add_ally\`/\`add_enemy\` guidance above), never skip creation or rename to avoid a collision.
- The same typo/near-homophone/visually-similar-character reasoning also applies *within* a single input, even without the creature list: if a name mentioned once (e.g. in a target list) reappears later in a visually similar but not identical form (e.g. "怨恨2" vs "怨魂2"), and the surrounding context makes it clear they refer to the same entity, treat them as the same entity and use one consistent name across all resulting tool calls. As with the list-based case, only do this when you're confident — if two similar names could plausibly be different entities, don't merge them.

# Example
Input: "尤里卡 风暴+突袭-10mp 怨恨2+怨恨3 暗影突袭（太刀）：掷骰【1D8 (6) + 1D8 (1) + 4】 命中值：【11】，伤害值：HR (6) + 16 = 【22】。"
Correct tool calls: adjust_mp(sequence=1, name="尤里卡", amount=-10), adjust_hp(sequence=2, name="怨恨2", amount=-22), adjust_hp(sequence=3, name="怨恨3", amount=-22)
Why: "风暴+突袭" (before the MP cost) and "暗影突袭（太刀）" (right before the dice roll) both describe the SAME single attack — an ability name plus a restated/flavor name with its weapon noted in parentheses — not two separate attacks or buffs. It costs 10 MP and hits both listed targets ("怨恨2+怨恨3") for the one damage number stated once ("22"), applied to each target individually. Multiple ability-name-like phrases and a "+" joining two target names in the same sentence do not mean there are multiple separate events to record — read the whole sentence as one attack with two targets before deciding how many tool calls it produces.`;

// Minimal local shape of an OpenAI-compatible tool definition (DeepSeek uses
// the same schema); not worth pulling in the full SDK type for this.
interface ChatCompletionTool {
  type: "function";
  function: {
    name: string;
    description: string;
    parameters: {
      type: "object";
      properties: Record<string, unknown>;
      required?: string[];
    };
  };
}

const kSequenceProperty = {
  type: "integer",
  description: "1-based order of this action relative to the other tool calls in this turn.",
} as const;

const TOOLS: ChatCompletionTool[] = [
  {
    type: "function",
    function: {
      name: "add_ally",
      description: "Add an allied creature with explicit stats given directly by the user. maxHp/maxMp/maxIp come from the user's text, not computed.",
      parameters: {
        type: "object",
        properties: {
          sequence: {
            type: "integer",
            description: "1-based order of this action relative to the other tool calls in this turn. The array position of tool_calls is NOT reliable — the server sorts by this field before executing.",
          },
          name: { type: "string" },
          maxHp: { type: "integer" },
          maxMp: { type: "integer" },
          maxIp: { type: "integer", minimum: 6, description: "Maximum Inventory Points (IP, 库存点上限/最大IP) for this ally. Minimum 6. Omit when unstated (defaults to 6). This is not the current, spendable IP." },
          dex: { type: "integer" },
          ins: { type: "integer" },
          mig: { type: "integer" },
          wlp: { type: "integer" },
          def: { type: "integer", description: "Defense modifier, or the fixed defense value if fixedDef is true." },
          mDef: { type: "integer", description: "Magic defense modifier." },
          fixedDef: { type: "boolean", default: false },
        },
        required: ["name", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "add_enemy",
      description: "Add an enemy creature. Provide raw lvl/mig/wlp/rank/extra_hp/extra_mp — the server computes maxHp/maxMp via the fixed calculateEnemyHpMp formula. Never compute maxHp/maxMp yourself.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
          lvl: { type: "integer" },
          mig: { type: "integer" },
          wlp: { type: "integer" },
          dex: { type: "integer" },
          ins: { type: "integer" },
          rank: { type: "string", enum: ["minion", "elite", "champion2", "champion3"] },
          extra_hp: { type: "integer", default: 0 },
          extra_mp: { type: "integer", default: 0 },
          def: { type: "integer" },
          mDef: { type: "integer" },
          fixedDef: { type: "boolean", default: false },
        },
        required: ["name", "lvl", "mig", "wlp", "rank", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "remove_creature",
      description: "Remove an existing creature from combat.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
        },
        required: ["name", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "adjust_hp",
      description: "Add (positive) or remove (negative) HP from a creature.",
      parameters: {
        type: "object",
        properties: {
          sequence: {
            type: "integer",
            description: "1-based order of this action relative to the other tool calls in this turn. Order matters: HP is clamped to maxHp, so +10 then -5 can yield a different final value than -5 then +10.",
          },
          name: { type: "string" },
          amount: { type: "integer" },
        },
        required: ["name", "amount", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "adjust_mp",
      description: "Add (positive) or remove (negative) MP from a creature.",
      parameters: {
        type: "object",
        properties: {
          sequence: {
            type: "integer",
            description: "1-based order of this action relative to the other tool calls in this turn. MP is clamped to maxMp, so order affects the final value.",
          },
          name: { type: "string" },
          amount: { type: "integer" },
        },
        required: ["name", "amount", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "adjust_ip",
      description: "Add (positive) or remove (negative) Inventory Points from an ALLY creature. Enemies never have IP.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
          amount: { type: "integer" },
        },
        required: ["name", "amount", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "set_status",
      description: "Add or remove a status effect from a creature.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
          status: { type: "string", enum: ["weak", "slow", "enraged", "dazed", "poisoned", "shaken"] },
          add: { type: "boolean", description: "true to apply the status, false to remove it." },
        },
        required: ["name", "status", "add", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "set_buff",
      description: "Add or remove a buff. buff_name is a free-text string, either 'defFixedN'/'mDefFixedN' (N = a fixed defense/magic-defense value) or any other plain-text buff description.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
          add: { type: "boolean" },
          buff_name: { type: "string", description: "e.g. 'defFixed12', 'mDefFixed8', or free text like '命中加2'." },
        },
        required: ["name", "add", "buff_name", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "set_study",
      description: "Set an enemy's study level.",
      parameters: {
        type: "object",
        properties: {
          sequence: kSequenceProperty,
          name: { type: "string" },
          level: { type: "string", enum: ["null", "7+", "10+", "13+"] },
        },
        required: ["name", "level", "sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "clear_all",
      description: "Remove all creatures from combat.",
      parameters: {
        type: "object",
        properties: { sequence: kSequenceProperty },
        required: ["sequence"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "noop",
      description: "Nothing happened as a result of this input.",
      parameters: {
        type: "object",
        properties: { sequence: kSequenceProperty },
        required: ["sequence"],
      },
    },
  },
];

// Argument shapes for each tool, used by the server to type-narrow
// JSON.parse(tool_call.function.arguments) before translating to a Command.
interface AddAllyArgs {
  sequence: number;
  name: string;
  maxHp?: number;
  maxMp?: number;
  maxIp?: number;
  dex?: number;
  ins?: number;
  mig?: number;
  wlp?: number;
  def?: number;
  mDef?: number;
  fixedDef?: boolean;
}

interface AddEnemyArgs {
  sequence: number;
  name: string;
  lvl: number;
  mig: number;
  wlp: number;
  rank: "minion" | "elite" | "champion2" | "champion3";
  dex?: number;
  ins?: number;
  extra_hp?: number;
  extra_mp?: number;
  def?: number;
  mDef?: number;
  fixedDef?: boolean;
}

interface RemoveCreatureArgs {
  sequence: number;
  name: string;
}

interface AdjustHpArgs {
  sequence: number;
  name: string;
  amount: number;
}

interface AdjustMpArgs {
  sequence: number;
  name: string;
  amount: number;
}

interface AdjustIpArgs {
  sequence: number;
  name: string;
  amount: number;
}

type StatusEffectName = "weak" | "slow" | "enraged" | "dazed" | "poisoned" | "shaken";

interface SetStatusArgs {
  sequence: number;
  name: string;
  status: StatusEffectName;
  add: boolean;
}

interface SetBuffArgs {
  sequence: number;
  name: string;
  add: boolean;
  buff_name: string;
}

interface SetStudyArgs {
  sequence: number;
  name: string;
  level: "null" | "7+" | "10+" | "13+";
}

interface ClearAllArgs {
  sequence: number;
}

interface NoopArgs {
  sequence: number;
}

// Discriminated union covering every tool call the model can make.
type ToolCall =
  | { name: "add_ally"; args: AddAllyArgs }
  | { name: "add_enemy"; args: AddEnemyArgs }
  | { name: "remove_creature"; args: RemoveCreatureArgs }
  | { name: "adjust_hp"; args: AdjustHpArgs }
  | { name: "adjust_mp"; args: AdjustMpArgs }
  | { name: "adjust_ip"; args: AdjustIpArgs }
  | { name: "set_status"; args: SetStatusArgs }
  | { name: "set_buff"; args: SetBuffArgs }
  | { name: "set_study"; args: SetStudyArgs }
  | { name: "clear_all"; args: ClearAllArgs }
  | { name: "noop"; args: NoopArgs };

// Mirrors the formula documented in docs/dialogflow-cx-playbook.md /
// docs/deepseek-system-prompt.md. The model never computes hp/mp itself — it
// only supplies the raw fields; this is the deterministic server-side step.
function calculateEnemyHpMp(input: {
  lvl: number;
  mig: number;
  wlp: number;
  rank: string;
  extra_hp?: number;
  extra_mp?: number;
}): { hp: number; mp: number } {
  const baseHp = input.lvl * 2 + input.mig * 5 + (input.extra_hp ?? 0);
  const baseMp = input.lvl + input.wlp * 5 + (input.extra_mp ?? 0);
  switch (input.rank) {
    case "elite":
      return { hp: baseHp * 2, mp: baseMp };
    case "champion2":
      return { hp: baseHp * 2, mp: baseMp * 2 };
    case "champion3":
      return { hp: baseHp * 3, mp: baseMp * 2 };
    default:
      return { hp: baseHp, mp: baseMp };
  }
}

export {
  SYSTEM_PROMPT,
  TOOLS,
  calculateEnemyHpMp,
  type ChatCompletionTool,
  type ToolCall,
  type AddAllyArgs,
  type AddEnemyArgs,
  type RemoveCreatureArgs,
  type AdjustHpArgs,
  type AdjustMpArgs,
  type AdjustIpArgs,
  type SetStatusArgs,
  type SetBuffArgs,
  type SetStudyArgs,
  type ClearAllArgs,
  type NoopArgs,
  type StatusEffectName,
};
